import React, { ReactNode } from 'react';
import {
  Box, Flex, Heading, Spacer, Text, useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useAppearance } from '../theme/AppearanceProvider';

// The ONE page-header used across every screen: doc grids, full-page shells, the
// add/edit document form, etc. Previously this block was copy-pasted into 4 files,
// each drifting its own spacing — and the icon chip used an 18px OUTWARD glow halo
// that bled rightward into the title (the "text over the icon" bug). Here the chip
// glow is CONTAINED (accent ring + inset glow, no outward halo), so the gap to the
// title is honest and the title can never sit on the icon. Change it once, here.
interface PageHeaderProps {
  // a rendered icon node (e.g. <BrandIcon src={...} color="currentColor" boxSize="26px" />);
  // the chip tints it via currentColor, so pass color="currentColor" where it applies
  icon: ReactNode;
  title: ReactNode;
  // optional supporting line under the title (e.g. "8 records")
  subtitle?: ReactNode;
  // right-aligned slot — an ADD button, a close (X), etc.
  actions?: ReactNode;
  // item 313/353 — pin the page header just BELOW the global dashboard/KPI strip while
  // the page scrolls (content slides under it).
  sticky?: boolean;
  // item 353b — the quieter, smaller title treatment (Chad: "I liked the older one"):
  // md heading + small chip; used by the entity forms' pinned titles.
  compact?: boolean;
  // item 538 — the TIGHTEST treatment: a slim project-view SECTION bar (small chip, sm
  // title, single-line feel). Even more condensed than compact.
  dense?: boolean;
  // item 553 — when set, the icon + title cluster becomes a click target (e.g. a
  // collapsible section: click the whole title, not just the tiny chevron, to toggle).
  onTitleClick?: () => void;
  // item 732 (Chad) — when this is a collapsible section, PageHeader renders ONE uniform chevron
  // (part of the clickable bar, so it can't double-toggle) instead of each section drawing its own
  // at different sizes. Pass the collapsed state; omit for non-collapsible headers.
  collapsed?: boolean;
  // item 732 (Chad) — override the section row's flat panel with a gradient (e.g. the brand-red
  // Project Management bucket header). Wins over the default row background.
  rowGradient?: string;
  // item 647 — optional content rendered BELOW the title row, inside the same (sticky) card.
  // Used by the RNWY guided flow to pin the step progress bar in the EDIT PROPOSAL header so it
  // stays visible while scrolling the steps.
  belowBar?: ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  icon, title, subtitle, actions, sticky, compact, dense, onTitleClick, collapsed, rowGradient, belowBar,
}) => {
  const { aurora } = useAppearance();
  const headingColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const subColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const chipBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  // item 732 (Chad) — the pinned page header is the brand-accent (red) bar, not a second gray
  // panel. On that saturated ground the chip + title go white; the glassy green-chip treatment is
  // reserved for non-sticky/inline headers. (Brand-driven, so white-label tenants tint their own.)
  // item 958a (Chad: "EDIT PROPOSAL"/"EDIT INVOICE" white-on-white in LIGHT mode) — the
  // item-732 red-bar treatment (brand gradient + white text) is a DARK-mode look; in light
  // Aurora the pinned header goes light-glass with ink text so it can never wash out.
  const darkMode = useColorModeValue(false, true);
  const redHeader = sticky && aurora && darkMode;
  // item 732 (Chad) — with the aurora gradient hidden, collapsible SECTION rows need their own
  // delineation: a subtle panel + hover so each reads as a distinct row on the flat ground.
  const isSectionRow = !!onTitleClick;
  const rowBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const rowHoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');

  // item 732 — chip + title colors adapt: white on the red brand header, green-glass chip on
  // inline aurora headers, ink on the Accountant skin (kept out of JSX to avoid nested ternaries).
  let chipBgVal = 'ledgerSurfaceAlt';
  let chipColorVal = 'ledgerInk';
  let chipBorderVal = 'ledgerStroke';
  let headingColorVal = 'ledgerInk';
  if (redHeader) {
    chipBgVal = 'whiteAlpha.300'; chipColorVal = 'white'; chipBorderVal = 'whiteAlpha.500'; headingColorVal = 'white';
  } else if (aurora) {
    chipBgVal = chipBg; chipColorVal = 'brandGreen.400'; chipBorderVal = 'brandGreen.400'; headingColorVal = headingColor;
  }
  const chipShadow = (!redHeader && aurora) ? 'inset 0 0 16px -8px var(--chakra-colors-brandGreen-400)' : 'none';
  const subColorVal = redHeader ? 'whiteAlpha.800' : subColor;

  // when pinned, the wrapper owns the bottom gap; the inner row sits flush. item 538 —
  // compact (project-view SECTION) headers get a tight gap so a section reads as a slim
  // bar, not a bulky card; the large standalone page title keeps its airy gap.
  // size tiers (largest → tightest): default page title, compact form title, dense section
  // item 732 (Chad) — density pass: the default page title was too tall (48px chip / lg heading /
  // airy gaps) and ate vertical pixels on every screen. Trimmed to a 38px chip + md heading with
  // tighter gaps; compact/dense tiers nudged down in step.
  let barMb: number | { base: number; md: number } = { base: 4, md: 5 };
  let chip = '34px';
  let chipFont = '18px';
  let headingSize = 'md';
  // item 732 (Chad) — explicit title font size, ~4px smaller than the Chakra size default per tier
  let headingFontSize = '0.8rem';
  let iconMr: number | { base: number; md: number } = { base: 3, md: 3 };
  let subFont = 'sm';
  if (dense) {
    // item 732 (Chad) — UX-tuned section row: ~46px tall, 13.6px title, 26px chip — condensed but
    // comfortably scannable + clickable (the 12px/22px version was too small).
    chip = '26px'; chipFont = '14px'; headingSize = 'sm'; headingFontSize = '0.85rem'; iconMr = { base: 2, md: 3 }; subFont = 'xs';
  } else if (compact) {
    chip = '30px'; chipFont = '15px'; headingSize = 'sm'; headingFontSize = '0.72rem';
  }
  if (sticky) barMb = 0;
  else if (dense) barMb = 1;
  else if (compact) barMb = 2;

  // item 553/732 — the icon + title cluster. The click-to-toggle target is now the WHOLE header
  // bar (see `bar` below), so the cluster itself is plain.
  const titleCluster = (
    <Flex
      align="center"
      // item 1089 (Chad's phone pass) — flex:1 has flex-basis 0, so this cluster never
      // forced a wrap: wide action clusters crushed it to ~0px and the uppercase title
      // stacked one letter per line ("A-C-C-O-U-N-T-A-N-T"). A real minimum makes the
      // actions wrap BELOW the title instead.
      minW={{ base: 'min(200px, 60%)', md: 0 }}
      flex="1 1 auto"
    >
      {icon && (
        <Flex
          w={chip}
          h={chip}
          flexShrink={0}
          align="center"
          justify="center"
          fontSize={chipFont}
          // explicit margin (Chakra 1.6 doesn't reliably apply `gap` on Flex) so the
          // title can never hug the icon
          mr={iconMr}
          borderRadius={aurora ? 'xl' : 'md'}
          bg={chipBgVal}
          color={chipColorVal}
          borderWidth="1px"
          borderColor={chipBorderVal}
          // contained: a crisp accent ring (the border) + an INSET glow — no outward
          // halo to bleed into the title (that bleed was what kept the text "on" the icon)
          boxShadow={chipShadow}
        >
          {icon}
        </Flex>
      )}
      <Box minW={0}>
        {/* item 406 — solid titles: the green gradient was on EVERY page title (and the
            hero, and every stat) — when everything glows, nothing does. The gradient is
            now reserved for the dashboard hero + stat values; titles read crisper too
            (bgClip text blurs slightly at lg).
            item 732 (Chad) — DENSE section headers put the count INLINE with the title so each
            collapsed project section is a single-line, ~half-height row (fit more on screen). */}
        {dense && subtitle !== undefined && subtitle !== null ? (
          <Flex align="baseline" minW={0} gridGap={2}>
            {/* item 732 (Chad) — the TITLE never truncates (flexShrink 0); the subtitle shrinks +
                truncates instead, so headers like PROJECT MANAGEMENT / PROJECT TALK show in full. */}
            <Heading size={headingSize} fontSize={headingFontSize} color={headingColorVal} textTransform="uppercase" whiteSpace="nowrap" flexShrink={0}>{title}</Heading>
            <Text fontSize={subFont} color={subColorVal} noOfLines={1} minW={0} display={{ base: 'none', sm: 'block' }}>{subtitle}</Text>
          </Flex>
        ) : (
          <>
            {/* item 732 (Chad) — normalize casing: every header title is UPPERCASE so sections
                don't mix 'Customer' with 'PROPOSALS'. One place, can't drift. */}
            <Heading size={headingSize} fontSize={headingFontSize} color={headingColorVal} textTransform="uppercase">{title}</Heading>
            {subtitle !== undefined && subtitle !== null && (
              <Text fontSize={subFont} color={subColorVal} noOfLines={2} wordBreak="break-word">{subtitle}</Text>
            )}
          </>
        )}
      </Box>
    </Flex>
  );

  const bar = (
    // item 1063 — the header bar WRAPS on narrow screens so action clusters drop below
    // the title instead of crushing it
    <Flex
      position="relative"
      zIndex={1}
      align="center"
      wrap="wrap"
      sx={{ rowGap: '8px' }}
      mb={barMb}
      // item 732 (Chad) — a subtle row panel so sections are delineated on the flat (gradient-off)
      // ground; only for collapsible section rows, not page titles. A rowGradient (e.g. the brand-red
      // bucket header) wins over the flat panel.
      bg={isSectionRow && !rowGradient ? rowBg : undefined}
      bgGradient={isSectionRow ? rowGradient : undefined}
      borderRadius={isSectionRow ? 'lg' : undefined}
      px={isSectionRow ? { base: 3, md: 4 } : undefined}
      py={isSectionRow ? 2.5 : undefined}
      {...(onTitleClick ? {
        // item 732 (Chad) — click ANYWHERE on the header row to toggle the section (not just the
        // tiny chevron); the actions cluster stops propagation so its buttons still work.
        onClick: onTitleClick,
        cursor: 'pointer',
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTitleClick(); }
        },
        _hover: { bg: rowHoverBg },
        transition: 'background 0.15s ease',
      } : {})}
    >
      {titleCluster}
      {actions && <Spacer />}
      {/* item 732 (Chad) — on the red brand header, action buttons become clean OUTLINE-white
          (transparent + white ring/text) so they read as intentional header actions on the
          saturated ground, not a random solid-white block (overrides per-page colorSchemes). */}
      {actions && (
        <Box
          // item 732 (Chad) — CRITICAL: stop clicks on the actions (chevron, ADD, etc.) from
          // bubbling to the bar's toggle handler. Without this the chevron fired BOTH its own
          // toggle AND the bar toggle → double-toggle → the section never opened.
          onClick={(e) => e.stopPropagation()}
          sx={redHeader ? {
            '& .chakra-button': {
              background: 'transparent',
              color: 'white',
              borderWidth: '1px',
              borderColor: 'var(--chakra-colors-whiteAlpha-700)',
            },
            '& .chakra-button:hover': { background: 'var(--chakra-colors-whiteAlpha-200)' },
          } : undefined}
        >
          {actions}
        </Box>
      )}
      {/* item 732 (Chad) — the ONE uniform collapse chevron, owned by PageHeader (not each section)
          so every section's arrow is identical. It's part of the clickable bar (not in the
          stop-propagation actions box), so clicking it toggles via the bar — no double-toggle. */}
      {onTitleClick && collapsed !== undefined && (
        <Box
          as={ChevronDownIcon}
          ml={3}
          boxSize="18px"
          flexShrink={0}
          opacity={0.7}
          transform={collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'}
          transition="transform 0.2s ease"
        />
      )}
    </Flex>
  );

  if (!sticky) {
    return (
      <>
        {bar}
        {belowBar && <Box>{belowBar}</Box>}
      </>
    );
  }

  // item 361 — pinned at its NATURAL resting position, PERMANENTLY: the shell scrolls
  // content in its own region below the persistent dashboard (SidebarWithHeader header
  // slot), so this sticks to the scrollport at a CONSTANT offset = the content pane's
  // top padding (12/16px — keep in sync with SidebarWithHeader's content pt). Nothing
  // is measured, so nothing can drift (the fix for the 313/353 regression cycle).
  // The card is INSET within the page padding (like the strip's card) with rounded 2xl
  // corners — content scrolls under and shows around it, same as under the strip.
  // item 568 — the 12/16px offset above the floating card was RAW scrollport: form text
  // scrolled through it fully readable (Chad's EDIT PROPOSAL bleed-through). The sticky
  // region now starts at 0 and FROSTS everything beneath it (blur + faint tint in Aurora;
  // solid paper in Accountant), while the card inside keeps its inset floating look and
  // the red brand rule. Nothing readable ever slides between the bars again.
  return (
    <Box
      position="sticky"
      top={0}
      zIndex={14}
      pt={{ base: '6px', md: '8px' }}
      // item 780 (Chad) — content CUTS OFF at the header instead of reading through the
      // wrapper's transparent gap/corner notches (732 removed the frost band; this replaces
      // it with the page's own canvas color, so it reads as "content disappears", not a box).
      // The bottom 12px of the wrapper fades canvas→transparent so scrolled content
      // DISSOLVES under the card edge rather than popping in at a hard line; part of the
      // old mb is converted to pb so total spacing below the card is unchanged.
      pb="12px"
      mb={{ base: '4px', md: '8px' }}
      bg={aurora ? undefined : 'appHeaderBacking'}
      sx={aurora ? {
        background: 'linear-gradient(to bottom, var(--bx-app-canvas) calc(100% - 12px), transparent 100%)',
      } : undefined}
    >
      <Box
        px={{ base: 3, md: 5 }}
        // item 732 (Chad) — shorter pinned bar
        py={compact ? 1 : { base: 1.5, md: 2 }}
        borderRadius="2xl"
        // item 732 (Chad) — the header IS the brand-accent (red) bar now, not a gray panel.
        // Brand-driven gradient so white-label tenants tint their own chrome.
        bg={(() => {
          if (!aurora) return 'ledgerSurface';
          return darkMode ? undefined : 'whiteAlpha.800'; // item 958a — light glass in light mode
        })()}
        bgGradient={aurora && darkMode ? 'linear(135deg, brand.500, brand.700)' : undefined}
        border="1px solid"
        borderColor={(() => {
          if (!aurora) return 'ledgerStroke';
          return darkMode ? 'whiteAlpha.300' : 'blackAlpha.200'; // item 958a
        })()}
        boxShadow={(() => {
          if (!aurora) return 'none';
          return darkMode
            ? '0 10px 34px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.22)'
            : '0 8px 24px rgba(0,0,0,0.08)'; // item 958a — soft light shadow
        })()}
      >
        {bar}
        {belowBar && <Box mt={compact ? 2 : 3}>{belowBar}</Box>}
      </Box>
    </Box>
  );
};

PageHeader.defaultProps = {
  subtitle: undefined,
  actions: undefined,
  sticky: false,
  compact: false,
  dense: false,
  onTitleClick: undefined,
  collapsed: undefined,
  rowGradient: undefined,
  belowBar: undefined,
};

export default PageHeader;
